import { keycloak } from '../auth/keycloak'
import { env } from '../config/env'
import { BFF_PATHS } from '../config/constants'
import { catalogs, type MessageKey } from '../i18n/messages'
import { invoiceItemId, validateInvoiceFile, INVOICE_PATTERN, type InvoiceReference } from './budgetInvoices'

// Minimal shape of Form.io's Providers registry — passed in (not imported) so
// @formio/js stays in the lazy form chunk and out of the main bundle.
type ProvidersStatic = {
  addProvider: (type: string, name: string, provider: () => unknown) => void
}

// Response of POST {BFF}/docloan/upload. The file is already in LoanPro when
// this returns — there is no uploadUrl and nothing further to do.
type UploadResult = {
  customerId: number
  docId: string
  fileName: string
  uploaded: boolean
  invoice?: InvoiceReference
}

/**
 * Localized message outside React: this module isn't a component, so it can't
 * use useI18n(). I18nProvider mirrors the active locale to <html lang>, and the
 * catalogs are a plain import — resolve through those, falling back to English.
 */
function localize(key: MessageKey): string {
  const locale = document.documentElement.lang as keyof typeof catalogs
  return catalogs[locale]?.[key] ?? catalogs.en[key]
}

/**
 * Chrome-native previewable file formats. Reviewers inspect documents in-browser
 * without downloading, so uploads are restricted to formats Chrome natively
 * renders (PDF and standard web images).
 */
const ALLOWED_EXTENSIONS = new Set(['pdf', 'png', 'jpg', 'jpeg', 'webp', 'gif', 'bmp'])
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024 // 10 MB limit

export function validateFileForUpload(file: File): void {
  const ext = file.name.split('.').pop()?.toLowerCase() ?? ''
  if (!ALLOWED_EXTENSIONS.has(ext)) {
    throw new Error(localize('upload.invalidFileType'))
  }
  if (file.size > MAX_FILE_SIZE_BYTES) {
    throw new Error(localize('upload.fileTooLarge'))
  }
}

/**
 * Tag every loanpro file component with its own key as `dir`, so the provider
 * can recover the docKey at upload time. Form.io never passes the component
 * instance to storage providers — but it does pass `dir` (from `component.dir`,
 * interpolated; plain keys pass through unchanged) as uploadFile's third
 * argument. The BFF requires this docKey to route the file into the right KYC
 * review slot. Mutates in place — call it on a fresh schema before caching.
 * Recurses nested `components` arrays (panels hold their fields there), the
 * same shape devUnlockForm walks.
 */
export function tagLoanproDocKeys(components: unknown[]): void {
  for (const component of components) {
    if (!component || typeof component !== 'object') continue
    const c = component as {
      storage?: string
      key?: string
      dir?: string
      filePattern?: string
      fileMaxSize?: string
      components?: unknown[]
      columns?: unknown[]
      rows?: unknown[]
    }
    if (c.storage === 'loanpro') {
      if (c.key && !c.dir) c.dir = c.key
      // Restrict file chooser dialog to Chrome-native previewable formats
      c.filePattern = c.key === 'invoice' ? INVOICE_PATTERN : '.pdf,.png,.jpg,.jpeg,.webp,.gif,.bmp'
      c.fileMaxSize = '10MB'
    }
    if (Array.isArray(c.components)) tagLoanproDocKeys(c.components)
    if (Array.isArray(c.columns)) {
      for (const col of c.columns) {
        if (col && typeof col === 'object' && Array.isArray((col as { components?: unknown[] }).components)) {
          tagLoanproDocKeys((col as { components?: unknown[] }).components!)
        }
      }
    }
    if (Array.isArray(c.rows)) {
      for (const row of c.rows) {
        if (Array.isArray(row)) {
          for (const cell of row) {
            if (cell && typeof cell === 'object' && Array.isArray((cell as { components?: unknown[] }).components)) {
              tagLoanproDocKeys((cell as { components?: unknown[] }).components!)
            }
          }
        }
      }
    }
  }
}

/** Must return a fresh (non-expired) Keycloak access token, or uploads 401. */
async function getAccessToken(): Promise<string> {
  await keycloak.updateToken(30)
  if (!keycloak.token) throw new Error('Not authenticated — no Keycloak token for upload')
  return keycloak.token
}

/**
 * One network call per file: POST {BFF}/docloan/upload (authenticated,
 * multipart/form-data) — the BFF redeems the presigned URL and PUTs the bytes
 * to LoanPro server-side, so no presigned URL ever reaches the browser.
 */
async function uploadToLoanPro(file: File, fileName: string, docKey?: string) {
  const itemId = invoiceItemId(docKey)
  if (itemId) validateInvoiceFile(file)
  // Validate file type (Chrome-native previewable only) and file size before any network request
  validateFileForUpload(file)

  const token = await getAccessToken()

  const form = new FormData()
  form.append('file', file) // part name must be exactly "file" or the BFF replies 400 file_required
  form.append('fileName', fileName)
  form.append('customFileName', fileName)
  // The file component's key — routes the upload into its KYC review slot.
  // Omitted (rather than sent empty) when tagging failed, so the BFF's
  // docKey_required error stays a loud integration-bug signal.
  if (docKey) form.append('docKey', docKey)

  const res = await fetch(`${env.VITE_BFF_URL}${BFF_PATHS.docloanUpload}`, {
    method: 'POST',
    cache: 'no-store',
    // Authorization only. Do NOT set Content-Type — the browser must generate
    // the multipart boundary itself, or the BFF replies 400 file_required.
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  })
  if (!res.ok) {
    // Form.io shows the thrown message on the file field, so a friendly message
    // here is the whole UX. A `failed` doc is re-uploadable through the same
    // component — no extra handling needed beyond surfacing the reason.
    const detail = await res.text().catch(() => '')
    let code: string | undefined
    try {
      code = (JSON.parse(detail) as { error?: string }).error
    } catch {
      // non-JSON error body — fall through to the generic message
    }
    if (code === 'section_not_open') throw new Error(localize('upload.sectionNotOpen'))
    if (code === 'doc_already_passed') throw new Error(localize('upload.docAlreadyPassed'))
    if (code === 'document_under_review') throw new Error(localize('upload.documentUnderReview'))
    if (code === 'budget_not_editable') throw new Error(localize('upload.budgetNotEditable'))
    throw new Error(`LoanPro upload failed (${res.status}): ${detail}`)
  }
  const result = (await res.json()) as UploadResult
  if (itemId && (!result.uploaded || result.invoice?.budgetItemId !== itemId || !result.invoice.docId)) {
    throw new Error(localize('upload.invoiceNotConfirmed'))
  }

  return { docId: result.docId, customerId: result.customerId, storedName: result.fileName, invoice: result.invoice }
}

let registered = false

/**
 * Register the custom "loanpro" Form.io storage provider. The EDF form's ~14 file
 * fields use storage: "loanpro"; without this they break at render. Idempotent —
 * safe to call on every form mount.
 */
export function registerLoanproStorage(Providers: ProvidersStatic) {
  if (registered) return
  registered = true

  Providers.addProvider('storage', 'loanpro', function loanproProvider() {
    return {
      title: 'LoanPro',
      name: 'loanpro',

      // Form.io calls this once per attached file. The returned object is what
      // Form.io stores into the submission for that file field. `dir` is the
      // third positional arg (component.dir, interpolated) — tagLoanproDocKeys
      // sets it to the component's key so it arrives here as the docKey.
      async uploadFile(file: File, fileName: string, dir?: string) {
        const { docId, customerId, storedName, invoice } = await uploadToLoanPro(file, fileName, dir)
        if (invoice) return invoice
        return {
          storage: 'loanpro',
          name: fileName,
          originalName: file.name,
          size: file.size,
          type: file.type,
          docId, // LoanPro document id
          customerId, // LoanPro customer id
          loanproFileName: storedName,
          url: '', // no public URL; download is brokered separately if ever needed
        }
      },

      // Invoice files are fetched through the authenticated BFF. Other KYC
      // fields keep their existing reference-only download behavior.
      async downloadFile(fileRef: unknown) {
        const ref = fileRef as Partial<InvoiceReference>
        if (!ref.budgetItemId || !ref.docId) return fileRef
        const token = await getAccessToken()
        const path = `/me/budget/items/${encodeURIComponent(ref.budgetItemId)}/invoices/${encodeURIComponent(ref.docId)}/file`
        const response = await fetch(`${env.VITE_BFF_URL}${path}`, {
          cache: 'no-store',
          headers: { Authorization: `Bearer ${token}` },
        })
        if (!response.ok) throw new Error(localize('upload.invoiceOpenFailed'))
        const url = URL.createObjectURL(await response.blob())
        // Give the renderer time to open the authenticated file, then release it.
        window.setTimeout(() => URL.revokeObjectURL(url), 60_000)
        return { ...ref, url }
      },

      deleteFile() {
        return Promise.resolve()
      },
    }
  })
}
